import { AgTechDataAction, DataActionResults } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechApiEntityResponse, AgTechHttpRequest, convertHttpResponse } from "@agtech/core/data/actions/AgTechHttpRequests";
import { useAgTechApiDownloadRequest } from "@agtech/core/data/http/AgTechApiGetRequests";
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { useLotDataMarkLotAsInvoicedAction } from "../lots/LotDataActions";
import { InvoiceDataGetBidderLotsToInvoiceQuery, InvoiceDataGetUnpaidInvoicesQuery, InvoiceDataGetUnprocessedSalesQuery } from "./InvoicingDataQueries";
import { useCurrentSale } from "../sales/SaleDataStore";
import { getInvoiceFileName } from "./InvoicingData";
import { AgTechContact } from "@agtech/data/contacts/ContactData";
import { LotDataGetLotsQuery } from "../lots/LotDataQueries";
import { InvoiceDataGetInvoiceLotsQuery } from "../lots/invoices/LotInvoiceDataQueries";

/// DTOs

type CreateInvoicePostDTO = {
    saleId: number;
    invoiceNumber?: string,
    checkNumber: string,
    bidderId: number,
    contactId: number,
    date: Date,
    lots: number[],
    discounts: number[],
    credits: number[]
}

export type InvoiceDocumentGetDTO = {
    invoiceId: number,
    fileName: string
}

/// Invoicing Actions

export type CreateInvoiceActionData = {
    invoiceNumber?: string,
    checkNumber: string,
    bidderId: number,
    contactId: number,
    lots: number[]
    discounts: number[],
    credits: number[],
    date: Date,
    contact: AgTechContact
}

export type InvoiceCreationResult = {
    invoiceId: number,
    invoiceDocument: Blob | undefined
}

export const useInvoiceDataCreateInvoiceAction = (): AgTechDataAction<CreateInvoiceActionData, InvoiceCreationResult> => {
    const currentSale = useCurrentSale();
    const submitInvoicePostRequest = useAgTechHttpPostRequest<CreateInvoicePostDTO, AgTechApiEntityResponse<number>>();

    const markLotAsInvoicedAction = useLotDataMarkLotAsInvoicedAction();
    const getInvoiceDocumentRequest = useInvoiceDataDownloadInvoiceDocumentHttpRequest();

    return {
        name: 'CreateInvoice',
        getConfiguration: () => ({
            actionExecutionMessage: 'Processing invoice...',
            actionConfirmationMessage: `Successfully created new invoice`
        }),
        validate: async props => {
            // props.validation.failWithWarningIf(props.submittedEntity.invoiceNumber === '', 'Please specify an invoice number.');
            props.validation.failWithWarningIf(props.submittedEntity.lots.length === 0, 'Please specify one or more lots to invoice');
        },
        action: async props => {
            const invoiceCreationResponse = await submitInvoicePostRequest.post({
                path: `Invoice/SubmitForProcessSale`,
                postedData: {
                    saleId: currentSale.id,
                    bidderId: props.submittedEntity.bidderId,
                    contactId: props.submittedEntity.contactId,
                    invoiceNumber: props.submittedEntity.invoiceNumber ?? "",
                    checkNumber: props.submittedEntity.checkNumber,
                    date: props.submittedEntity.date,
                    credits: props.submittedEntity.credits,
                    discounts: props.submittedEntity.discounts,   
                    lots: props.submittedEntity.lots,
                }
            })
            .then(res => convertHttpResponse(res));

            // const invoiceCreationResponse = {
            //     success: {
            //         data: 61
            //     }
            // }

            if (invoiceCreationResponse.success) {
                const invoicePdfFileName = getInvoiceFileName({
                    invoiceContact: props.submittedEntity.contact,
                    invoiceNumber: props.submittedEntity.invoiceNumber ?? "",
                    invoiceDate: props.submittedEntity.date.toDateString()
                });
                
                const getInvoiceDocumentResponse = await getInvoiceDocumentRequest.execute({
                    data: {
                        invoiceId: invoiceCreationResponse.success?.data,
                        fileName: invoicePdfFileName
                    }
                });

                if (getInvoiceDocumentResponse.success) {
                    return DataActionResults.Success({
                        invoiceId: invoiceCreationResponse.success.data,
                        invoiceDocument: getInvoiceDocumentResponse.success.data
                    });
                }
            }

            return DataActionResults.Failed('Unable to create the invoice, please try again.'); 
        },
        onSuccess: async props => {
            await props.actionExecutor.executeAction(markLotAsInvoicedAction, {
                submittedEntity: {
                    lotIds: props.submittedEntity.lots
                }
            });

            // Updates the unprocessed sales page
            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });

            // Update unpaid invoices to show the new invoice
            props.invalidateQuery({
                query: InvoiceDataGetUnpaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });

            // Update lot data to reflect the newly invoiced lots
            props.invalidateQuery({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id }
            });

            // Update lots available for invoicing by this bidder
            props.invalidateQuery({
                query: InvoiceDataGetBidderLotsToInvoiceQuery,
                queryContext: { bidderId: props.submittedEntity.bidderId }
            });
        }
    }
}

export const useInvoiceDataDownloadInvoiceAction = (): AgTechDataAction<InvoiceDocumentGetDTO, Blob> => {
    const invoiceDownloadRequest = useAgTechApiDownloadRequest();
    
    return {
        name: 'DownloadInvoiceDocument',
        getConfiguration: () => ({
            actionExecutionMessage: 'Downloading invoice...',
            actionConfirmationMessage: `Successfully downloaded invoice`
        }),
        action: async props => {
            return await await invoiceDownloadRequest.download({
                path: 'Invoice/GetInvoiceDocument/' + props.submittedEntity.invoiceId,
                fileNameWithExtension: props.submittedEntity.fileName
            });
        },
    }
}

export const useInvoiceDataDownloadInvoiceImagesAction = (): AgTechDataAction<InvoiceDocumentGetDTO, Blob> => {
    let downloadInvoiceRequest = useInvoiceDataDownloadInvoiceDocumentHttpRequest();

    return {
        name: 'DownloadInvoiceImages',
        getConfiguration: () => ({
            actionExecutionMessage: 'Downloading invoice...',
            actionConfirmationMessage: `Successfully downloaded invoice`
        }),
        action: async props => {
            return await downloadInvoiceRequest.execute({
                data: props.submittedEntity
            });
        },
    }
}

/// Downloads

export const useInvoiceDataDownloadInvoiceDocumentHttpRequest = (): AgTechHttpRequest<InvoiceDocumentGetDTO, Blob> => {
    let invoiceDownloadRequest = useAgTechApiDownloadRequest();
    
    return {
        execute: async props => {
            return await invoiceDownloadRequest.download({
                path: 'Invoice/GetInvoiceDocument/' + props.data.invoiceId,
                fileNameWithExtension: props.data.fileName
            });
        }
    }
}